import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import SubmitDropdown from "./SubmitDropdown";
import { useAuthUser } from "../lib/useAuthUser";
import { useVendorFlags } from "../lib/useVendorFlags";
import { startGoogleLogin, authLogout, resetAuthUser } from "../lib/api";

const API = process.env.REACT_APP_BACKEND_URL;

function InstallNav() {
  const { user, loading } = useAuthUser();
  // Logged-in users go straight to the install page; logged-out users are sent
  // to Google sign-up first (open signup) and land on /install afterwards.
  if (!loading && !user) {
    return (
      <button
        type="button"
        onClick={() => startGoogleLogin("/install")}
        className="tv-link"
        data-testid="nav-install"
      >
        install
      </button>
    );
  }
  return (
    <Link to="/install" className="tv-link" data-testid="nav-install">
      install
    </Link>
  );
}

function AuthControl() {
  const { user, loading } = useAuthUser();
  if (loading) return null;
  if (!user) {
    return (
      <button
        type="button"
        onClick={() => startGoogleLogin("/")}
        className="tv-link"
        data-testid="nav-login"
      >
        log in
      </button>
    );
  }
  const label = (user.name || user.email || "").split(" ")[0] || "account";
  const signOut = async () => {
    try {
      await authLogout();
    } catch {
      /* no active session */
    }
    resetAuthUser();
    window.location.reload();
  };
  return (
    <span className="flex items-center gap-1.5" data-testid="nav-account">
      <MyListingNav />
      <span className="text-[#000] font-bold" title={user.email}>{label}</span>
      <span className="text-[#888]">·</span>
      <button type="button" onClick={signOut} className="tv-link" data-testid="nav-logout">
        log out
      </button>
    </span>
  );
}

function MyListingNav() {
  const { flags } = useVendorFlags();
  if (!flags.claiming) return null;
  return (
    <>
      <Link to="/vendor" className="tv-link" data-testid="nav-my-listing">my listing</Link>
      <span className="text-[#888]">·</span>
    </>
  );
}

export default function Header() {
  return (
    <header className="border-b border-black bg-white" data-testid="site-header">
      <div className="max-w-5xl mx-auto px-4 py-3 sm:flex sm:items-center sm:justify-between sm:gap-4">
        <Link to="/" className="no-underline shrink-0 block sm:inline-block" data-testid="home-link">
          <span className="text-black font-bold text-lg tracking-tight whitespace-nowrap">
            TerminalValue<span className="text-[#ff6600]">.io</span>
            <span className="tv-blink ml-1 text-black">_</span>
          </span>
        </Link>
        <nav className="mt-2 sm:mt-0 flex flex-wrap sm:flex-nowrap items-center justify-between sm:justify-end gap-x-2 gap-y-2 sm:gap-3 text-sm">
          <div className="flex items-center gap-1.5 sm:gap-3 text-xs sm:text-sm">
            <Link to="/" className="tv-link" data-testid="nav-home">index</Link>
            <span className="text-[#888]">|</span>
            <InstallNav />
            <span className="text-[#888]">|</span>
            <Link to="/about" className="tv-link" data-testid="nav-about">about</Link>
            <span className="text-[#888]">|</span>
            <AuthControl />
          </div>
          <SubmitDropdown />
        </nav>
      </div>
      <Marquee />
    </header>
  );
}

function Marquee() {
  const [count, setCount] = useState(null);
  useEffect(() => {
    // Lightweight count fetch — cached briefly, fails silently. Falls
    // back to no number rendered (rest of the strip still shows).
    fetch(`${API}/api/stats`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => {
        if (d && typeof d.tools === "number") setCount(d.tools);
      })
      .catch(() => {});
  }, []);

  const lines = [
    count != null
      ? { key: "count", node: (
          <span data-testid="marquee-tool-count">manually approved · {count} tools indexed</span>
        ) }
      : null,
    "since 2026",
  ].filter(Boolean);

  return (
    <div className="border-t border-[#ddd] bg-[#fafafa] overflow-hidden">
      <div className="max-w-5xl mx-auto px-4 py-1 tv-marquee flex items-center gap-3 overflow-hidden whitespace-nowrap">
        <span className="text-[#000]">{">>"}</span>
        {lines.map((l, i) => {
          const isObj = typeof l === "object";
          const key = isObj ? l.key : l;
          const node = isObj ? l.node : l;
          return (
            <span key={key} className="text-[#555]">
              {node}
              {i < lines.length - 1 && <span className="ml-3 text-[#bbb]">·</span>}
            </span>
          );
        })}
      </div>
    </div>
  );
}
